import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { FeatureFlag } from "@markeroo/shared";
import { apiAxios } from "@/api/config";

type FeatureFlagContextType = {
  flags: Record<string, boolean>;
  isLoading: boolean;
  isEnabled: (key: string) => boolean;
  reload: () => Promise<void>;
};

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export function FeatureFlagProvider({
  children,
  initialFlags = {},
  skipFetch = false,
}: {
  children: ReactNode;
  initialFlags?: Record<string, boolean>;
  skipFetch?: boolean;
}) {
  const [flags, setFlags] = useState<Record<string, boolean>>(initialFlags);
  const [isLoading, setIsLoading] = useState(!skipFetch);

  const fetchFlags = useCallback(async () => {
    try {
      const response = await apiAxios.get<{ items: FeatureFlag[] }>("/feature-flags");
      const flagMap: Record<string, boolean> = {};
      response.data.items.forEach((flag) => {
        flagMap[flag.key] = flag.enabled;
      });
      setFlags(flagMap);
    } catch {
      // Silently fail - flags will default to false
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skipFetch) {
      return;
    }
    void fetchFlags();
  }, [fetchFlags, skipFetch]);

  const isEnabled = useCallback(
    (key: string): boolean => {
      return flags[key] ?? false;
    },
    [flags]
  );

  const value: FeatureFlagContextType = useMemo(
    () => ({
      flags,
      isLoading,
      isEnabled,
      reload: fetchFlags,
    }),
    [flags, isLoading, isEnabled, fetchFlags]
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export function useFeatureFlags(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagProvider");
  }
  return context;
}

export function useFeatureFlag(key: string, defaultValue = false): boolean {
  const { flags, isLoading } = useFeatureFlags();
  if (isLoading) {
    return defaultValue;
  }
  return flags[key] ?? defaultValue;
}
